import { useState } from 'react'

let recentHookToken: { route: string; token: string } | undefined

const WALL_ROUTE = '/wall'
const RAW_WALL_TOKEN = /^[0-9a-f]{64}$/

export const WALL_VIEWER_SESSION_KEY = 'recordbreaker-matrix-codex:wall-viewer:v1'

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null
  try { return window.sessionStorage } catch { return null }
}

function removeWallViewerSession(storage: Storage | null = sessionStorageOrNull()): void {
  try { storage?.removeItem(WALL_VIEWER_SESSION_KEY) } catch { /* Storage may be blocked by browser policy. */ }
}

export function clearWallViewerToken(): void {
  removeWallViewerSession()
}

export function consumeFragmentToken(fragment = window.location.hash) {
  const params = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment)
  const token = params.get('t')
  if (!token) return null
  params.delete('t')
  const remaining = params.toString()
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`)
  if (token.length < 32 || token.length > 256) return null
  return token
}

export function useFragmentToken() {
  const [token] = useState(() => {
    const consumed = consumeFragmentToken()
    if (consumed) {
      recentHookToken = { route: `${window.location.pathname}${window.location.search}`, token: consumed }
      queueMicrotask(() => { recentHookToken = undefined })
      return consumed
    }
    return recentHookToken?.route === `${window.location.pathname}${window.location.search}` ? recentHookToken.token : null
  })
  return token
}

/**
 * A wall URL is itself the bearer credential. Keep the canonical token in the
 * fragment so refresh, bookmarks, and a newly opened computer retain access,
 * while never copying it into Web Storage or sending it in an HTTP request.
 */
export function useWallFragmentToken() {
  const [token] = useState(() => {
    removeWallViewerSession()
    if (window.location.pathname !== WALL_ROUTE) return null
    const match = /^#t=([0-9a-f]{64})$/.exec(window.location.hash)
    if (match?.[1] && RAW_WALL_TOKEN.test(match[1])) return match[1]
    if (window.location.hash) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    return null
  })
  return token
}
